/*
 * gtm-core.js
 * ---------------------------------------------------------------
 * Reusable foundation for all GA4 dataLayer events on this store.
 * Contains NO event definitions - those live in gtm-events.js.
 *
 * Public API:
 *   GTM.ctx                 raw Liquid context (window.__gtmCtx)
 *   GTM.money(cents)        1999 -> 19.99
 *   GTM.item(src, extra)    normalize any Shopify object -> GA4 item
 *   GTM.value(items)        sum of price * quantity
 *   GTM.push(name, items)   push a GA4 ecommerce event
 *   GTM.onAjax(re, fn)      run fn(json, url) when a cart URL responds
 *   GTM.cart                cached line snapshot, keyed by line key
 *   GTM.listFor(productId)  stored list_name / list_id / index
 *   GTM.rememberList(id, o) store list context for a product
 *   GTM.debug = true        log every push to the console
 * ---------------------------------------------------------------
 */
window.GTM = (function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var ctx = window.__gtmCtx || {};
  var api = {
    ctx: ctx,
    debug: false,
    currency: ctx.currency || 'USD',
    cart: {}
  };

  /* ---------- helpers ---------- */

  api.money = function (cents) {
    return Number(((Number(cents) || 0) / 100).toFixed(2));
  };

  function num(v) {
    return Number((Number(v) || 0).toFixed(2));
  }

  /**
   * Normalize a Shopify line item (from /cart/*.js or Liquid) into a
   * GA4 item object. Cent-based fields are auto-detected and converted.
   */
  api.item = function (src, extra) {
    if (!src) return {};
    // Shopify AJAX responses expose final_price / original_price in CENTS.
    // Liquid context (gtm-context.liquid) already divides by 100.
    var cents = (src.final_price != null || src.original_price != null);
    var raw = src.final_price != null ? src.final_price : src.price;
    var orig = src.original_price != null ? src.original_price : raw;

    var item = {
      item_id:      String(src.item_id || src.sku || src.variant_id || src.id || ''),
      item_name:    src.item_name || src.product_title || src.title || '',
      item_brand:   src.item_brand || src.vendor || '',
      item_variant: src.item_variant || src.variant_title || '',
      item_category: src.item_category || src.product_type || '',
      price:        cents ? api.money(orig) : num(orig),
      quantity:     Number(src.quantity) || 1
    };

    var disc = cents ? api.money(orig - raw) : num(orig - raw);
    if (disc > 0) item.discount = disc;

    // merge stored list context, then explicit overrides
    var list = api.listFor(src.product_id || src.item_id);
    if (list) {
      if (list.item_list_name) item.item_list_name = list.item_list_name;
      if (list.item_list_id)   item.item_list_id   = list.item_list_id;
      if (list.index)          item.index          = list.index;
    }
    if (extra) Object.keys(extra).forEach(function (k) {
      if (extra[k] !== '' && extra[k] != null) item[k] = extra[k];
    });

    // strip empties so GA4 doesn't record blank strings
    Object.keys(item).forEach(function (k) {
      if (item[k] === '' || item[k] == null) delete item[k];
    });
    return item;
  };

  // GA4 convention: `price` is pre-discount, `discount` is per-unit,
  // so event value must be (price - discount) * quantity.
  api.value = function (items) {
    return num(items.reduce(function (s, i) {
      var net = (Number(i.price) || 0) - (Number(i.discount) || 0);
      return s + net * (Number(i.quantity) || 1);
    }, 0));
  };

  /* ---------- the single push point for every event ---------- */

  api.push = function (eventName, items, overrides) {
    if (!eventName) return;
    var list = [].concat(items || []).filter(Boolean);
    var ecommerce = {
      currency: api.currency,
      value: api.value(list)
    };
    if (list.length) ecommerce.items = list;
    if (overrides) Object.keys(overrides).forEach(function (k) {
      ecommerce[k] = overrides[k];
    });

    window.dataLayer.push({ ecommerce: null });      // clear previous ecommerce object
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });

    if (api.debug) console.log('[GTM]', eventName, ecommerce);
  };

  /* ---------- list context (item_list_name / id / index) ---------- */

  api.rememberList = function (productId, data) {
    try { sessionStorage.setItem('gtm_list_' + productId, JSON.stringify(data)); }
    catch (e) {}
  };

  api.listFor = function (productId) {
    if (!productId) return null;
    try { return JSON.parse(sessionStorage.getItem('gtm_list_' + productId)); }
    catch (e) { return null; }
  };

  /* ---------- cart snapshot, for detecting increases / decreases ---------- */

  function snapshot(lines) {
    var map = {};
    (lines || []).forEach(function (li) {
      map[li.key || li.variant_id] = li;
    });
    return map;
  }

  api.cart = snapshot(ctx.cart && ctx.cart.items);

  api.updateCart = function (lines) {
    api.cart = snapshot(lines);
  };

  /**
   * Compare a fresh cart against the cached one.
   * Returns { added: [lines], removed: [lines] } with quantity = the delta.
   */
  api.diffCart = function (lines) {
    var next = snapshot(lines), added = [], removed = [];
    Object.keys(api.cart).forEach(function (k) {
      var before = api.cart[k], after = next[k];
      var d = (after ? after.quantity : 0) - before.quantity;
      if (d < 0) {
        removed.push(Object.assign({}, before, { quantity: Math.abs(d) }));
      } else if (d > 0) {
        added.push(Object.assign({}, after, { quantity: d }));
      }
    });
    Object.keys(next).forEach(function (k) {
      if (!api.cart[k]) added.push(next[k]);
    });
    api.updateCart(lines);
    return { added: added, removed: removed };
  };

  /* ---------- AJAX interceptor registry ---------- */

  var handlers = [];

  api.onAjax = function (pattern, fn) {
    handlers.push({ re: pattern, fn: fn });
  };

  function dispatch(url, json) {
    handlers.forEach(function (h) {
      if (h.re.test(url)) {
        try { h.fn(json, url); } catch (e) { if (api.debug) console.warn('[GTM]', e); }
      }
    });
  }

  var _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function () {
      var url = String((arguments[0] && (arguments[0].url || arguments[0])) || '');
      return _fetch.apply(this, arguments).then(function (res) {
        if (res && res.ok && /\/cart\//.test(url)) {
          res.clone().json().then(function (j) { dispatch(url, j); }).catch(function () {});
        }
        return res;
      });
    };
  }

  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    var u = String(url || '');
    this.addEventListener('load', function () {
      if (this.status < 400 && /\/cart\//.test(u)) {
        try { dispatch(u, JSON.parse(this.responseText)); } catch (e) {}
      }
    });
    return _open.apply(this, arguments);
  };

  return api;
})();
